/* global gtag */
import axios from 'axios';
import { Mutex } from 'async-mutex';
import store from './store/store';

const instance = axios.create({
  timeout: 0, // TODO: Choose a reasonable timeout amount
  headers: { Pragma: 'no-cache', 'X-Frame-Options': 'SAMEORIGIN', 'X-XSS-Protection': '1; mode=block', 'Cache-Control': 'no-store' }, // Force IE 11 to not cache the response
});

instance.CancelToken = axios.CancelToken;
instance.isCancel = axios.isCancel;

window.cancel = () => {}; // Stub for method that may or may not be modified

switch (process.env.NODE_ENV) {
  case 'local':
    // axios.defaults.baseURL = 'http://localhost:53140/';
    axios.defaults.baseURL = 'https://agentportal-dev.api.insurance.com/';
    break;
  case 'development':
    axios.defaults.baseURL = 'https://agentportal-dev.api.insurance.com/';
    break;
  case 'testing':
    axios.defaults.baseURL = 'https://agentportal-test.api.insurance.com/';
    break;
  case 'stage':
    axios.defaults.baseURL = 'https://agentportal-stage.api.insurance.com/';
    break;
  case 'prod':
  case 'production':
    axios.defaults.baseURL = 'https://agentportal.api.insurance.com/';
    break;
  default:
    axios.defaults.baseURL = 'https://agentportal-dev.api.insurance.com/';
}

axios.defaults.withCredentials = true; // Enable axios post cookie, default false

instance.appTimestamp = new Date().getTime(); // Store the timestamp of the app's creation
sessionStorage.removeItem('fileHash'); // Reset to avoid false positives on first pass

/**
 * Inform the user when there is a newer version of the app.
 *
 * The app must manually check if there are newer versions of its files because the
 * app's files are only requested in the initial page request. Therefore, we periodically
 * check if there is a newer version of the app's root file and, if needed, inform the
 * user to reload the page to get the newer version.
 *
 * @param {*} config Config object in the callback of `instance.interceptors.response.use`
 */
instance.checkNewestBuild = () => {
  const timeIntervalToTriggerCheck = 30000; // TODO: Use config value
  const pathToCheckIfNewer = `${window.location.origin}/?fileHash=1`; // TODO: Use config value
  const currentTime = new Date().getTime();

  // Short-curcuit if the time interval has not elapsed.
  if ((currentTime - instance.appTimestamp) < timeIntervalToTriggerCheck) {
    return;
  }

  instance.appTimestamp = currentTime; // Reset to current time since we're checking

  axios.get(pathToCheckIfNewer).then((response) => { // TODO: Do a HEAD request
    let newFileHash = null;

    if (response && response.headers && response.headers.etag) {
      newFileHash = response.headers.etag;
    } else if (response && response.headers && response.headers['last-modified']) {
      newFileHash = response.headers['last-modified'];
    } else if (response && response.data) {
      // The minified response data (i.e. HTML) is unique if filenamess have hashes
      const matchedRegex = response.data.match(/\/static\/js\/app\.(.*?)(\.js?)/g); // TODO: Use config value
      newFileHash = (matchedRegex !== null && Array.isArray(matchedRegex) !== undefined ? matchedRegex.pop() : '');
      window.console.warn('No etag, no last-modified. Using `response.data` to fingerprint the app');
    }

    // Only set `fileHash` on the first pass
    if (!sessionStorage.getItem('fileHash')) {
      sessionStorage.setItem('fileHash', newFileHash);
    }

    if (newFileHash !== sessionStorage.getItem('fileHash')) {
      store.dispatch('setUpdatedApp', true); // Inform the user to reload via alert messaging in the UI
      window.console.warn('Please reload your browser window. There is a newer version of the app.');
      gtag('event', 'exception', {
        description: 'Found newer version',
        fatal: false, // set to true if the error is fatal
      });
    }
  });
};

instance.refreshToken = () => {
  const refreshToken = store.getters.getRefreshToken();

  return axios.post(
    '/refreshToken', {
      grantType: 'refresh_token',
      refreshToken,
    },
  ).then((response) => {
    // Store new refresh token
    sessionStorage.setItem('refresh_token', response.data.refresh_token);

    // Store new access token
    sessionStorage.setItem('accessToken', response.data.access_token);

    // Store new expires at
    sessionStorage.setItem('expires_at', response.data['.expires'] * 1000); // Time access token expires, in milliseconds

    return response;
  }).catch((error) => {
    window.console.warn('Failed to refresh token', error);
    gtag('event', 'exception', {
      description: 'Failed to refresh token',
      fatal: false, // set to true if the error is fatal
    });

    return Promise.reject(error);
  });
};

instance.isAccessTokenExpired = () => {
  const refreshEarlyBy = 60000; // TODO: Use config value
  const refreshIn = sessionStorage.getItem('expires_at') - Date.now() - refreshEarlyBy; // Milliseconds until refresh
  window.console.info(`Refresh access token in: ${refreshIn / 1000} seconds.`);

  return (refreshIn < 1);
};

instance.logOut = () => {
  gtag('event', 'exception', {
    description: 'Forced logout',
    fatal: false, // set to true if the error is fatal
  });

  store.commit('clearSessionData');
  store.dispatch('agentLogout', true);
};

const mutex = new Mutex(); // TODO: Don't create global variables. Add it to something within scope such as the axios instance.

/* set loader for all axios request   */
/* eslint-disable */
instance.interceptors.request.use(async function (config) {

  if (!process.env.IS_OKTA_LOGIN_ENABLED) {
    // Always acquire the lock in preparation for refreshing the token, which may or may not happen.
    await mutex.runExclusive(async () => {

      // Only refresh after access token is expired
      if (!(store.getters.getRefreshToken() && instance.isAccessTokenExpired())) return;

      await instance.refreshToken()
        .catch(error => instance.logOut(error));
    });
  }

  // Continue with the request
  if (
    window.location.pathname !== '/' &&
    window.location.pathname !== '/user/reset-password/update' // TODO: Use route name
  ) {
    /* eslint no-param-reassign: "error" */
    if (!process.env.IS_OKTA_LOGIN_ENABLED)
      config.headers.Authorization = store.getters.getToken(); // Update auth header to use the newest access token in case the refresh token got a new one
  }
  // window.console.info('Request has begun via the axios-config global instance.');

  if (!config.cancelToken) { // Don't trigger "loading" UI for requests that have a cancel token
    store.dispatch('setLoaderStatus', config);
  }
  return config;
}, function (error) {
  store.dispatch('setLoaderStatus', error);
});

instance.interceptors.response.use(function (config) {
  const apiPath = config.config.url.replace(config.config.baseURL, '');
  const apiPathToTriggerCheck = '/navs/'; // TODO: Use config value

  try {
    // For the specified path prefix, check if there is a newer version of the app.
    if (apiPath.indexOf(apiPathToTriggerCheck) === 0) {
      // TODO: Temporarily disable until the fingerprinting can be done via the JS file hash.
      instance.checkNewestBuild();
    }
  } catch (e) {
    window.console.warn('An error occurred while checking for a newer version of the app.');
    gtag('event', 'exception', {
      description: 'Failed update check',
      fatal: false, // set to true if the error is fatal
    });
  }

  // window.console.info('Response has begun via the axios-config (global?) instance.');
  store.dispatch('setNetworkStatus', true);
  store.dispatch('setNetworkMessageStatus', config.status);
  store.dispatch('setLoaderStatus', config);
  // window.console.info('Successful response has been received.');
  return config;
}, function (error) {
  let errorDescription = (error && error.response && error.response.data && error.response.data.error) ? error.response.data.error : 'Unknown server error';
  let response_status = (typeof error.response !== 'undefined' ? error.response.status : null);
  response_status = response_status || error.message;

  // Detects whether request was cancelled, which is not technically an error
  if (error.__CANCEL__) {
    errorDescription = error.message;
    response_status = 204; // 204 "No Content" response code for cancelled request
  }

  gtag('event', 'exception', {
    description: errorDescription,
    fatal: true, // set to true if the error is fatal
  });

  store.dispatch('setLoaderStatus', error); // Hide spinner   
  if (response_status === 401) { // TODO: Refactor to avoid repeating code
    if (!process.env.IS_OKTA_LOGIN_ENABLED)
      instance.logOut();
  } else  if (
   //if response code is 404 and handel404 true then don't show pop up else show pop up.
   
   // if reponse code is niether 400,403, 204 404 along with  , don't show pop up.

    !(response_status === 404 && store.getters.getHandle404) &&
    !(response_status === 400) &&
    !(response_status === 403) &&
    !(response_status === 204)
  ) {
    store.dispatch('setNetworkStatus', false);
    store.dispatch('setNetworkMessageStatus', response_status);
  }

  // window.console.warn('The server has responded with an error.');

  return Promise.reject(error);
});


export default instance;



// WEBPACK FOOTER //
// ./src/axios-config.js